import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Paperclip,
  Save,
  Send,
  Trash2,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import RichTextEditor from "../components/RichTextEditor";
import AudiencePicker from "../components/AudiencePicker";
import { isFullHtmlDocument } from "../htmlBody";
import { usePermission } from "../../../hooks/usePermission";
import { useAuth } from "../../../context/AuthContexts";
import { useMeQuery } from "../../auth/api/authApi";
import {
  useCreateMailCampaignMutation,
  useDeleteCampaignAttachmentMutation,
  useGetMailCampaignQuery,
  useSendMailCampaignMutation,
  useTestMailCampaignMutation,
  useUpdateMailCampaignMutation,
  audienceIsEmpty,
  pruneAudience,
  DEFAULT_MAILER,
  DEFAULT_CHANNEL,
  SMS_MAX_LENGTH,
  type Audience,
  type AudienceSummary,
  type CampaignMailer,
  type CampaignChannel,
} from "../api/mailApi";

const MAX_ATTACHMENTS = 5;
const MAX_FILE_MB = 10;

function humanSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Compose a mail-out: subject, body, attachments, groups.
 *
 * Saving and sending are separate acts. Sending always saves first, so what
 * goes out is what is on the screen — an admin who edits the body and hits Send
 * without saving would otherwise mail the previous draft.
 */
export default function ComposeCampaignPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useGetMailCampaignQuery(id!, { skip: !id });
  const campaign = data?.campaign;

  const { data: me } = useMeQuery();
  const myEmail: string = me?.user?.email ?? "";

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<Audience>({});
  const [personalise, setPersonalise] = useState(true);
  const [mailer, setMailer] = useState<CampaignMailer>(DEFAULT_MAILER);
  const [channel, setChannel] = useState<CampaignChannel>(DEFAULT_CHANNEL);
  const isSms = channel === "sms";
  // What a text will actually be, so the counter measures the message rather
  // than the editor's markup — the API strips the tags before sending.
  const smsText = body.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const [testTo, setTestTo] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [summary, setSummary] = useState<AudienceSummary | null>(null);
  const [banner, setBanner] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [create, { isLoading: creating }] = useCreateMailCampaignMutation();
  const [update, { isLoading: updating }] = useUpdateMailCampaignMutation();
  const [sendCampaign, { isLoading: sending }] = useSendMailCampaignMutation();
  const [testSend, { isLoading: testing }] = useTestMailCampaignMutation();
  const [removeAttachment] = useDeleteCampaignAttachmentMutation();

  const busy = creating || updating || sending || testing;

  // A pasted mailer goes out on its own, without the letterhead — so the
  // letterhead's greeting has nothing to attach to. Offering the switch anyway
  // would promise a "Dear …" line that never appears.
  const rawBody = isFullHtmlDocument(body);

  // Reaching this page only proves mail_campaigns.manage. Committing the
  // circular to its audience is a separate grant, so someone cleared to compose
  // can draft, attach and rehearse it, then hand it to an authorised sender.
  const { can } = usePermission();
  const { hasRole } = useAuth();
  const canSend = hasRole("super_admin") || can("mail_campaigns.send");

  // Prefill once. Re-running on every fetch would overwrite whatever the admin
  // has typed since, each time a mutation invalidates the campaign tag.
  useEffect(() => {
    if (!campaign || loaded) return;
    setSubject(campaign.subject ?? "");
    setBody(campaign.body ?? "");
    setAudience(campaign.audience ?? {});
    setPersonalise(campaign.personalise_greeting ?? true);
    setMailer(campaign.mailer ?? DEFAULT_MAILER);
    setChannel(campaign.channel ?? DEFAULT_CHANNEL);
    setLoaded(true);
  }, [campaign, loaded]);

  // A sent campaign is a record, not a draft — it is read at its report page.
  useEffect(() => {
    if (campaign && campaign.status !== "draft") {
      navigate(`/admin/mail/campaigns/${campaign.id}`, { replace: true });
    }
  }, [campaign, navigate]);

  const pickFiles = (chosen: FileList | null) => {
    if (!chosen) return;
    const incoming = Array.from(chosen);
    const already = files.length + (campaign?.attachments?.length ?? 0);

    const tooBig = incoming.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) {
      setBanner({ tone: "bad", text: `${tooBig.name} is larger than ${MAX_FILE_MB} MB.` });
      return;
    }
    if (already + incoming.length > MAX_ATTACHMENTS) {
      setBanner({ tone: "bad", text: `A message carries at most ${MAX_ATTACHMENTS} attachments.` });
      return;
    }
    setFiles([...files, ...incoming]);
  };

  const validate = (): string | null => {
    if (!subject.trim()) return "The message needs a subject.";
    if (!body.replace(/<[^>]*>/g, "").trim()) return "The message body is empty.";
    // Checked here as well as on the API so the length is refused while it can
    // still be edited, rather than after the draft round-trips.
    if (isSms && smsText.length > SMS_MAX_LENGTH) {
      return `A text message is limited to ${SMS_MAX_LENGTH} characters — this one is ${smsText.length}.`;
    }
    if (isSms && files.length > 0) {
      return "A text message cannot carry an attachment. Remove the files, or switch back to email.";
    }
    return null;
  };

  /** Persist and return the campaign id, or null if it could not be saved. */
  const persist = async (): Promise<string | null> => {
    const problem = validate();
    if (problem) {
      setBanner({ tone: "bad", text: problem });
      return null;
    }

    const payload = {
      subject,
      body,
      audience: pruneAudience(audience),
      personalise_greeting: personalise,
      mailer,
      channel,
      // An SMS carries none, and sending an empty array is clearer to the API
      // than sending files it is about to refuse.
      files: isSms ? [] : files,
    };

    try {
      if (id) {
        await update({ id, ...payload }).unwrap();
        setFiles([]);
        return id;
      }
      const created = await create(payload).unwrap();
      setFiles([]);
      // Same page, now anchored to a real draft, so Test and Send can act on it.
      navigate(`/admin/mail/compose/${created.campaign.id}`, { replace: true });
      return created.campaign.id;
    } catch (e: any) {
      setBanner({ tone: "bad", text: e?.data?.message || "The draft could not be saved." });
      return null;
    }
  };

  const onSaveDraft = async () => {
    const saved = await persist();
    if (saved) setBanner({ tone: "ok", text: "Draft saved." });
  };

  const onTest = async () => {
    const saved = await persist();
    if (!saved) return;
    try {
      // Blank means "my own account address", which is what the API defaults to.
      const res = await testSend({ id: saved, email: testTo.trim() || undefined }).unwrap();
      setBanner({ tone: "ok", text: res.message });
    } catch (e: any) {
      setBanner({ tone: "bad", text: e?.data?.message || "The test could not be sent." });
    }
  };

  const onSend = async () => {
    if (audienceIsEmpty(audience)) {
      setBanner({ tone: "bad", text: "Pick at least one group to send to." });
      return;
    }

    const count = summary?.total ?? 0;
    if (
      !window.confirm(
        `Send "${subject}" to ${count} recipient(s)?\n\n` +
          "This cannot be undone — the message goes out as soon as the queue picks it up.",
      )
    )
      return;

    const saved = await persist();
    if (!saved) return;

    try {
      const res = await sendCampaign(saved).unwrap();
      navigate(`/admin/mail/campaigns/${saved}`, { state: { justSent: res.message } });
    } catch (e: any) {
      setBanner({ tone: "bad", text: e?.data?.message || "The campaign could not be sent." });
    }
  };

  const onDeleteAttachment = async (attachmentId: string, filename: string) => {
    if (!id) return;
    if (!window.confirm(`Remove ${filename} from this message?`)) return;
    try {
      await removeAttachment({ id, attachmentId }).unwrap();
    } catch (e: any) {
      setBanner({ tone: "bad", text: e?.data?.message || "The attachment could not be removed." });
    }
  };

  if (id && isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 p-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading draft…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-4 md:p-6 pb-20">
      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <button
              onClick={() => navigate("/admin/mail/campaigns")}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <Mail className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">
                {id ? "Edit Message" : "Compose Message"}
              </h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">
                Write it, attach what it needs, choose who it goes to.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onSaveDraft}
              disabled={busy}
              className="inline-flex items-center gap-2 border bg-white rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-all active:scale-95 shadow-sm"
            >
              {creating || updating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Save draft
            </button>

            <button
              onClick={onTest}
              disabled={busy}
              title={`Send one copy to ${testTo.trim() || myEmail || "your account address"} before committing to the list`}
              className="inline-flex items-center gap-2 border bg-white rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-all active:scale-95 shadow-sm"
            >
              {testing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Mail className="w-3.5 h-3.5" />
              )}
              Send test
            </button>

            <button
              onClick={onSend}
              disabled={busy || audienceIsEmpty(audience) || !canSend}
              title={
                !canSend
                  ? "You may compose this campaign, but releasing it needs the send permission"
                  : audienceIsEmpty(audience)
                  ? "Pick at least one group first"
                  : `Send to ${summary?.total ?? 0} recipient(s)`
              }
              className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-primary disabled:opacity-40 transition-all active:scale-95 shadow-sm"
            >
              {sending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Send {summary?.total ? `to ${summary.total}` : ""}
            </button>
          </div>
        </div>
      </div>

      {banner && (
        <div
          className={`rounded-2xl px-5 py-3 flex items-center gap-3 border ${
            banner.tone === "ok"
              ? "bg-emerald-50 border-emerald-200"
              : "bg-rose-50 border-rose-200"
          }`}
        >
          {banner.tone === "ok" ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
          )}
          <p
            className={`text-xs font-bold ${
              banner.tone === "ok" ? "text-emerald-800" : "text-rose-800"
            }`}
          >
            {banner.text}
          </p>
          <button onClick={() => setBanner(null)} className="ml-auto">
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Message */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-4">
            {/* Email or text. First, because it changes what the rest of this
                form means — the subject stops being seen by anyone, the
                attachments go away, and the audience is counted on a different
                address. */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
                Send as
              </label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  { key: "email" as const, title: "Email", hint: "With a subject and attachments" },
                  { key: "sms" as const, title: "Bulk SMS", hint: "Plain text, to mobile numbers" },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setChannel(option.key)}
                    disabled={busy}
                    className={`text-left px-4 py-3 rounded-2xl border transition-all ${
                      channel === option.key
                        ? "bg-gray-900 text-white border-gray-900"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${busy ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <span className="block text-[10px] font-black uppercase tracking-[0.15em]">
                      {option.title}
                    </span>
                    <span
                      className={`block text-[10px] mt-1 ${
                        channel === option.key ? "text-white/60" : "text-gray-400"
                      }`}
                    >
                      {option.hint}
                    </span>
                  </button>
                ))}
              </div>
              {isSms && (
                <p className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                  Texts go to members with a mobile number on file — the count on the right is for
                  numbers, not addresses, so it will differ from the email total. Formatting and
                  attachments are dropped; only the words are sent.
                </p>
              )}
            </div>

            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
                {isSms ? "Subject — your own label, not sent" : "Subject"}
              </label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                placeholder="e.g. Notice of Annual General Meeting"
                className="mt-2 w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-gray-900 placeholder:text-gray-300"
              />
            </div>

            <div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
                  Message
                </label>

                {/* A circular that opens with its own "Dear Esteemed Member,"
                    must not be greeted twice by the letterhead. */}
                {rawBody ? (
                  <span className="text-[10px] font-bold text-gray-400">
                    Sent as pasted — greet the recipient with {"{{name}}"} in the HTML
                  </span>
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={personalise}
                      onChange={(e) => setPersonalise(e.target.checked)}
                      className="w-3.5 h-3.5 accent-gray-900"
                    />
                    <span className="text-[10px] font-bold text-gray-500">
                      Open with “Dear &lt;recipient&gt;,”
                    </span>
                  </label>
                )}
              </div>

              <div className="mt-2">
                <RichTextEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Write the message. It goes out on the Institute letterhead."
                />
              </div>

              <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
                Merge fields:{" "}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded font-bold text-gray-600">
                  {"{{name}}"}
                </code>{" "}
                becomes the recipient as filed (QS Adaeze Okonkwo),{" "}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded font-bold text-gray-600">
                  {"{{first_name}}"}
                </code>{" "}
                drops the courtesy title. Both fall back to “Esteemed Member” when
                the register has no name. Emoji are fine.
              </p>
            </div>
          </div>

          {/* Attachments */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
                Attachments
              </h2>
              <label className="inline-flex items-center gap-2 border bg-white rounded-xl px-4 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 hover:bg-gray-50 cursor-pointer transition-all">
                <Paperclip className="w-3.5 h-3.5" /> Add file
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    pickFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>

            <p className="text-[10px] text-gray-400 mt-2">
              Up to {MAX_ATTACHMENTS} files, {MAX_FILE_MB} MB each. Every recipient gets a copy of
              each file.
            </p>

            <div className="mt-4 space-y-2">
              {campaign?.attachments?.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-3 border border-gray-100 rounded-2xl px-4 py-3"
                >
                  <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-bold text-gray-700 hover:text-indigo-600 truncate"
                  >
                    {file.filename}
                  </a>
                  <span className="text-[10px] text-gray-400 ml-auto shrink-0">
                    {humanSize(file.size)}
                  </span>
                  <button
                    onClick={() => onDeleteAttachment(file.id, file.filename)}
                    className="p-1.5 rounded-lg hover:bg-rose-50 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                  </button>
                </div>
              ))}

              {files.map((file, i) => (
                <div
                  key={`${file.name}-${i}`}
                  className="flex items-center gap-3 border border-dashed border-indigo-200 bg-indigo-50/40 rounded-2xl px-4 py-3"
                >
                  <Paperclip className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                  <span className="text-xs font-bold text-gray-700 truncate">{file.name}</span>
                  <span className="text-[10px] text-indigo-500 font-black uppercase tracking-wider shrink-0">
                    not saved
                  </span>
                  <span className="text-[10px] text-gray-400 ml-auto shrink-0">
                    {humanSize(file.size)}
                  </span>
                  <button
                    onClick={() => setFiles(files.filter((_, x) => x !== i))}
                    className="p-1.5 rounded-lg hover:bg-rose-50 transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-rose-500" />
                  </button>
                </div>
              ))}

              {!campaign?.attachments?.length && files.length === 0 && (
                <p className="text-xs text-gray-300 py-4 text-center">Nothing attached.</p>
              )}
            </div>
          </div>
        </div>

        {/* Audience */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <h2 className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
              Send to
            </h2>
            <p className="text-[10px] text-gray-400 mt-2 mb-4">
              Tick any mix of groups. Anyone matched by more than one still gets a single copy.
            </p>
            <AudiencePicker
              value={audience}
              onChange={setAudience}
              onSummary={setSummary}
              disabled={busy}
              channel={channel}
            />
          </div>

          {/* Which sender the message leaves by. */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm mt-6">
            <h2 className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
              Send through
            </h2>
            <div className="mt-3 space-y-2">
              {[
                {
                  key: "cpanel" as const,
                  title: "Institute webmail",
                  badge: "Default",
                  hint: "Sends as the Secretariat mailbox — reads as ordinary correspondence. The host caps how many go out per hour, so use it for smaller sends.",
                },
                {
                  key: "brevo" as const,
                  title: "Brevo relay",
                  badge: null,
                  hint: "Built for volume. Shared sending domain, so a circular is likelier to be filed under Promotions. Worth switching to for a send large enough that the hourly cap bites.",
                },
              ].map((option) => (
                <label
                  key={option.key}
                  className={`block px-4 py-3 rounded-2xl border cursor-pointer transition-all ${
                    mailer === option.key
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white border-gray-200 hover:border-gray-300"
                  } ${busy ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <span className="flex items-center gap-2.5">
                    <input
                      type="radio"
                      name="mailer"
                      checked={mailer === option.key}
                      onChange={() => setMailer(option.key)}
                      className="w-3.5 h-3.5 accent-indigo-500"
                    />
                    <span className="text-[10px] font-black uppercase tracking-[0.15em]">
                      {option.title}
                    </span>
                    {option.badge && (
                      <span
                        className={`text-[9px] font-black uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${
                          mailer === option.key
                            ? "bg-white/15 text-white/70"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        {option.badge}
                      </span>
                    )}
                  </span>
                  <span
                    className={`block text-[10px] mt-1.5 leading-relaxed ${
                      mailer === option.key ? "text-gray-300" : "text-gray-400"
                    }`}
                  >
                    {option.hint}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Where the test copy goes — the address on your own admin account
              unless something else is typed here. */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm mt-6">
            <h2 className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
              Test copy
            </h2>
            <p className="text-[10px] text-gray-400 mt-2 mb-3">
              “Send test” delivers one copy — attachments and all — to{" "}
              <span className="font-bold text-gray-600">
                {testTo.trim() || myEmail || "your account address"}
              </span>
              . Nothing goes to the membership.
            </p>
            <input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder={myEmail || "your.address@example.com"}
              className="w-full border border-gray-200 rounded-2xl px-4 py-2.5 text-xs outline-none focus:border-gray-900 placeholder:text-gray-300"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
